import { format } from 'date-fns';

/**
 * Format a date that may not exist.
 *
 * WHY THIS EXISTS. A stage only gains `check_in_scheduled` when its check-in is
 * scheduled, which is the PUBLISHED-to-STARTED transition. A stage that is merely
 * published therefore never has one, by construction rather than by accident: 100 of
 * 100 published stages in production have it null (counted 2026-09-17), and a published
 * stage that never starts stays published forever - the oldest has sat there since 2020.
 *
 * Formatting that value unguarded fails in two different ways, and the harmless-looking
 * one is the worse bug:
 *
 *   new Date(undefined)  -> Invalid Date -> date-fns `format` THROWS RangeError, which
 *                           under React 19 takes down the whole surrounding region. This
 *                           is what blanked the client dashboard for every client whose
 *                           latest stage was published.
 *   new Date(null)       -> the Unix epoch, which is a VALID date, so nothing throws and
 *                           the interface confidently displays "January 1, 1970".
 *
 * The second is why a crash-only fix is not enough: the API sends this field as null, so
 * the sites that did not crash were quietly showing 1970 instead.
 *
 * Returns `fallback` (null by default, so a caller can render nothing) when there is no
 * usable date. Pass a string to show something in a table cell that must not be empty.
 */
export const formatDate = (value, pattern, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  // The epoch almost always means a null that JavaScript coerced, not a real 1970 date.
  if (date.getTime() === 0) return fallback;
  return format(date, pattern);
};

export default formatDate;
